// src/Components/Login.js
import React, { useEffect, useRef, useCallback } from "react";
import "./login.css";

/**
 * Login Component
 * - Secure Google Sign-In using GSI
 * - Legally compliant Terms + Privacy links
 * - Hardened against XSS, CSRF, and race conditions
 * - Safe cleanup on unmount
 */
const Login = () => {
  const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL?.trim() || "http://localhost:6005";
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim();
  const buttonRef = useRef(null);
  const scriptRef = useRef(null);

  /** Dynamically load Google GSI script safely */
  const loadGoogleScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      try {
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.crossOrigin = "anonymous";
        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error("Google GSI script failed to load"));
        document.body.appendChild(script);
        scriptRef.current = script;
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  /** Handle Google Sign-In response securely */
  const handleGoogleResponse = useCallback(
    async (response) => {
      if (!response?.credential) {
        console.error("❌ No credential received from Google");
        return;
      }

      try {
        const res = await fetch(`${BACKEND_URL}/ttc/auth/google/verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest", // CSRF protection
          },
          credentials: "include",
          body: JSON.stringify({ token: response.credential }),
        });

        const data = await res.json().catch(() => ({}));

        if (res.ok) {
          console.info("✅ Login success");
          window.location.assign("/ttc/dashboard");
        } else {
          console.error("❌ Login failed:", data?.error || data);
        }
      } catch (err) {
        console.error("❌ Network or verification error:", err);
      }
    },
    [BACKEND_URL]
  );

  /** Initialize GSI safely on mount */
  useEffect(() => {
    let mounted = true;

    if (!GOOGLE_CLIENT_ID) {
      console.error("❌ Missing GOOGLE_CLIENT_ID in environment");
      return;
    }

    loadGoogleScript()
      .then(() => {
        if (!mounted || !window.google?.accounts?.id) return;

        // Initialize with strict parameters
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          ux_mode: "popup",
          context: "signin",
        });

        if (buttonRef.current) {
          window.google.accounts.id.renderButton(buttonRef.current, {
            type: "standard",
            theme: "outline",
            size: "large",
            width: "200",
            shape: "pill",
            logo_alignment: "center",
            text: "continue_with",
          });
        }

        // Prompt returning users securely
        window.google.accounts.id.prompt();
      })
      .catch((err) => console.error("❌ Script load error:", err));

    // Cleanup
    return () => {
      mounted = false;
      if (scriptRef.current) {
        document.body.removeChild(scriptRef.current);
        scriptRef.current = null;
      }
    };
  }, [GOOGLE_CLIENT_ID, loadGoogleScript, handleGoogleResponse]);

  return (
    <div className="main_box">
      <div className="animate-gradient-text">
        <h1 style={{ padding: "20px" }}>TTChef</h1>
      </div>

      <h5>Turing test for Chef</h5>

      <div className="g_body">
        {/* Google Sign-In button mounts here */}
        <div ref={buttonRef} aria-label="Google Sign-In Button"></div>
      </div>

      <p
        style={{
          fontSize: "0.8rem",
          color: "#444",
          textAlign: "center",
          marginTop: "12px",
          lineHeight: "1.4",
        }}
      >
        By continuing, you agree to{" "}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#5162FF",
            textDecoration: "none",
            fontWeight: "500",
          }}
          aria-label="Google Terms of Service"
        >
          Google Terms of Service
        </a>{" "}
        and{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#5162FF",
            textDecoration: "none",
            fontWeight: "500",
          }}
          aria-label="Google Privacy Policy"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
};

export default React.memo(Login);
